import { notification } from "antd"
import type {
	ArgsProps,
	NotificationInstance,
} from "antd/es/notification/interface"
import { createContext, type ReactNode, useContext, useMemo } from "react"

const wrapNotify = (raw: NotificationInstance): NotificationInstance => {
	const wrapArgs = (args: ArgsProps): ArgsProps => {
		const { message, ...rest } = args as ArgsProps & { message?: ReactNode }
		return {
			title: args.title ?? message,
			...rest,
		}
	}
	const wrapped = { ...raw } as NotificationInstance
	const methods = ["open", "success", "error", "info", "warning"] as const
	for (const method of methods) {
		wrapped[method] = (args: ArgsProps) => raw[method](wrapArgs(args))
	}
	return wrapped
}

const defaultNotify = wrapNotify(notification)
const NotificationContext = createContext<NotificationInstance>(defaultNotify)

export function NotificationProvider({ children }: { children: ReactNode }) {
	const [notify, notifyElement] = notification.useNotification({
		placement: "top",
		stack: { threshold: 3 },
	})
	const wrappedNotify = useMemo(() => wrapNotify(notify), [notify])

	return (
		<NotificationContext value={wrappedNotify}>
			{children}
			{notifyElement}
		</NotificationContext>
	)
}

export const useNotification = () => {
	const notify = useContext(NotificationContext)

	const wrappedNotify = useMemo(() => {
		const result = { ...notify }

		METHODS.forEach((method) => {
			result[method] = (args: ArgsProps) => {
				notify[method]({
					...args,
					props: {
						role: "region",
						"aria-label": "Notification",
						...args?.props,
					},
				})
			}
		})

		return result
	}, [notify])

	return { notify: wrappedNotify }
}

type ToastMethod = "success" | "info" | "warning" | "error" | "open"

const METHODS: ToastMethod[] = ["success", "info", "warning", "error", "open"]
